import { useTranslation } from "react-i18next";
import { StyledDifficultySpace, StyledExerciseCardContainer, StyledExerciseCard, StyledExerciseCardImage, StyledExerciseCardLabel } from "./style";
import { ImageInputField, TextAreaField, TextInputField, DropdownField, StyledInputLabel, TagsDropdownField } from "../../../../../../components/inputs";
import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Divider, Flex, Form, notification, Spin } from "antd";
import { useAppDispatch, useAppSelector } from "../../../../../../store/config";
import { createExerciseStart, getExerciseStart, resetExercise, updateExerciseStart } from "../../../../../../store/features/exercise/exercise-slice";
import { fetchChosenExercisesStart, fetchExercisesStart } from "../../../../../../store/features/exercise/alt-exercises-slice";
import { AbsoluteButton35, RelativeFlex, StyledColumnWrapper, StyledForm, StyledLayout } from "../../../../../../components/common/styles";
import { IMAGE_BASE_URL, VIDEO_BASE_URL } from "../../../../../../common/config";
import { IUploadFileParams } from "../../../../../../interfaces/files/files";
import { ICreateExercisePayload } from "../../../../../../interfaces/store/exercise-interface";
import { useDifficultyOptions, useEquipmentTypeOptions, useMusclesOptions } from "../../../../useSelectOptions";
import { blobToBase64 } from "../../../../../../utils/utils";

export const SingleExercise = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [form] = Form.useForm();
  const { exerciseId } = useParams<{ exerciseId: string }>();
  const { id: trainerId } = useParams<{ id: string }>();
  const [api, contextHolder] = notification.useNotification();
  const { isEdit } = location.state || {};

  const isNew: boolean = location.pathname.includes("/exercises/add");

  const [isEditable, setIsEditable] = useState(false);
  const [selectedAltExercise, setSelectedAltExercise] = useState<string[]>([]);
  const [imageData, setImageData] = useState<IUploadFileParams | null>(null);
  const [videoData, setVideoData] = useState<IUploadFileParams | null>(null);

  const { exercise, loading, error, requestState, operation } = useAppSelector((state) => state.exercise);

  useEffect(() => {
    if (isNew || isEdit) {
      setIsEditable(true);
    }
    if (!isNew) {
      dispatch(getExerciseStart({ exerciseId: exerciseId || "" }));
    }

    return () => {
      dispatch(resetExercise());
    };
  }, [dispatch, isNew, exerciseId]);

  useEffect(() => {
    if (requestState === "finish" && exercise?._id) {
      if (operation === "update" || operation === "create") {
        setIsEditable(false);
        navigate(`/trainers/${trainerId}/exercises/${exercise._id}`, {
          replace: true,
        });
      }
    }
  }, [requestState, operation, exercise, navigate, trainerId]);

  useEffect(() => {
    if (exercise) {
      form.setFieldsValue({
        name: exercise.name,
        description: exercise.description,
        difficulty: exercise.difficulty,
        time: exercise.time,
        muscles: exercise.muscles,
        equipmentType: exercise.equipmentType,
        alternativeExercises: exercise.alternativeExercises,
      });
      setSelectedAltExercise(exercise.alternativeExercises || []);
    }
  }, [exercise]);

  useEffect(() => {
    if (error) {
      openNotification();
    }
  }, [error]);

  const openNotification = (message?: string, isWarning?: boolean) => {
    api[isWarning ? "warning" : "error"]({
      message: t(message || "Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  const upsertExercise = async (values: any) => {
    if (!isEditable) {
      setIsEditable(true);
      return;
    }

    const exercise = {
      name: values.name,
      description: values.description,
      difficulty: values.difficulty || null,
      time: values.time || null,
      muscles: values.muscles,
      equipmentType: values.equipmentType || null,
      alternativeExercises: values.alternativeExercises,
      trainer: trainerId || "",
    };

    const upsertExerciseBody: ICreateExercisePayload = {
      exercise: { ...exercise },
    };

    if (imageData) {
      const base64Image = await blobToBase64(imageData.binaryFile as Blob);
      upsertExerciseBody.imageData = {
        ...imageData,
        binaryFile: base64Image,
        fileType: "image",
      };
    }

    if (videoData) {
      const base64Video = await blobToBase64(videoData.binaryFile as Blob);
      upsertExerciseBody.videoData = {
        ...videoData,
        binaryFile: base64Video,
        fileType: "video",
      };
    }
    if (!videoData?.binaryFile) {
      openNotification("Please note that no video was provided for the exercise", true);
    }

    if (isNew) {
      dispatch(createExerciseStart({ ...upsertExerciseBody }));
    } else if (exerciseId) {
      dispatch(
        updateExerciseStart({
          ...upsertExerciseBody,
          exerciseId: exerciseId,
        })
      );
    }
  };

  const musclesOptions = useMusclesOptions();

  const equipmentTypeOptions = useEquipmentTypeOptions();

  return (
    <>
      {contextHolder}
      <RelativeFlex>
        <AbsoluteButton35 onClick={() => form.submit()}>{isEditable ? t("Save") : t("Edit")}</AbsoluteButton35>
        <Divider style={{ margin: "15px" }} />
      </RelativeFlex>

      <Spin spinning={loading}>
        <StyledForm form={form} onFinish={upsertExercise} validateTrigger="onSubmit">
          <StyledLayout>
            <StyledColumnWrapper>
              <TextInputField name="name" label={t("Exercise Name")} isEditable={isEditable} rules={[{ required: true, message: t("Required") }]} />
              <TextAreaField name="description" label={t("Description")} isEditable={isEditable} rules={[{ required: true, message: t("Required") }]} />
              <StyledDifficultySpace>
                <DropdownField label={t("Difficulty")} options={useDifficultyOptions()} value={exercise?.difficulty || undefined} isEditable={isEditable} name="difficulty" />
                <TextInputField
                  name="time"
                  label={t("Exercise Duration in Minutes")}
                  isEditable={isEditable}
                  rules={[
                    {
                      pattern: /^[0-9]*$/,
                      message: t("Invalid number of minutes"),
                    },
                  ]}
                />

                <DropdownField label={t("Equipment Type")} options={equipmentTypeOptions} isEditable={isEditable} name="equipmentType" />
              </StyledDifficultySpace>

              <TagsDropdownField mode="multiple" label={t("Muscles")} options={musclesOptions} name="muscles" isEditable={isEditable} rules={[{ required: true, message: t("Required") }]} />
              <AltExercises isEditable={isEditable} selectedAltExercise={selectedAltExercise} setSelectedAltExercise={setSelectedAltExercise} openNotification={openNotification} />
            </StyledColumnWrapper>
            <StyledColumnWrapper>
              <ImageInputField
                label={t("Exercise Image")}
                isEditable={isEditable}
                value={exercise?.image ? IMAGE_BASE_URL + "/" + exercise?.image : undefined}
                onChange={setImageData}
                aspect={83 / 86}
              />
              <ImageInputField
                label={t("Video")}
                isEditable={isEditable}
                value={exercise?.video ? VIDEO_BASE_URL + "/" + exercise?.video : undefined}
                onChange={setVideoData}
                type="video"
                aspect={1}
              />
            </StyledColumnWrapper>
          </StyledLayout>
        </StyledForm>
      </Spin>
    </>
  );
};

const AltExercises = (props: { isEditable: boolean; selectedAltExercise: string[]; setSelectedAltExercise: (values: string[]) => void; openNotification: () => void }) => {
  const { isEditable, selectedAltExercise, setSelectedAltExercise, openNotification } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { id: trainerId } = useParams<{ id: string }>();

  const { exercises, chosenExercises, loading, error } = useAppSelector((state) => state.altExercises);

  useEffect(() => {
    fetchExercises("");
  }, [trainerId, dispatch]);

  useEffect(() => {
    if (error) openNotification();
  }, [error]);

  useEffect(() => {
    dispatch(fetchChosenExercisesStart({ exercisesIds: selectedAltExercise }));
  }, [selectedAltExercise]);

  const handleAltExerciseChange = (values: string[] | unknown) => {
    if (Array.isArray(values)) {
      setSelectedAltExercise(values);
    }
  };

  // const removeAltExercise = (exerciseId: string) => {
  //   setSelectedAltExercise((prev: string[]) =>
  //     prev.filter((id: string) => id !== exerciseId)
  //   );
  // };

  const onBlur = () => {
    fetchExercises("");
  };

  const onSearch = (value: string) => {
    fetchExercises(value);
  };

  const fetchExercises = (search: string) => {
    dispatch(
      fetchExercisesStart({
        filter: { trainer: trainerId },
        search: search,
        sort: { sortBy: "updatedAt", sortOrder: -1 },
        pagination: {
          page: 0,
          limit: 20,
          skip: 0,
        },
      })
    );
  };

  const selectOptions = [
    ...exercises.map((exercise) => ({
      label: exercise.name || "",
      value: exercise._id || "",
    })),
    ...chosenExercises
      .filter((chosen) => !exercises.some((exercise) => exercise._id === chosen._id))
      .map((exercise) => ({
        label: exercise.name || "",
        value: exercise._id || "",
      })),
  ];

  return (
    <Spin spinning={loading}>
      <Flex vertical gap={24}>
        <DropdownField
          hidden={!isEditable}
          label={t("Alternative Exercises")}
          mode="multiple"
          options={selectOptions}
          isEditable={isEditable}
          onChange={handleAltExerciseChange}
          name="alternativeExercises"
          onSearch={onSearch}
          onBlur={onBlur}
        />
        <StyledExerciseCardContainer>
          {chosenExercises.length > 0 && <StyledInputLabel>{t("Alternative Exercises")}</StyledInputLabel>}
          {chosenExercises.map((altExercise) => {
            if (!altExercise) return null;

            const videoName = altExercise.video?.replace(/\.mp4$/, "") || "";

            const imgSrc = altExercise.image ? `${IMAGE_BASE_URL}/${altExercise.image}` : videoName ? `${VIDEO_BASE_URL}/${videoName}__thumb_regular.jpg` : undefined;

            return (
              <StyledExerciseCard key={altExercise._id}>
                {/* {isEditable && (
                <CloseCircleFilled
                  onClick={() => removeAltExercise(altExercise._id || "")}
                  style={{
                    color: "#e0e0e0",
                    fontSize: 20,
                  }}
                />
              )} */}
                <StyledExerciseCardImage src={imgSrc} />
                <StyledExerciseCardLabel>{altExercise.name}</StyledExerciseCardLabel>
              </StyledExerciseCard>
            );
          })}
        </StyledExerciseCardContainer>
      </Flex>
    </Spin>
  );
};
